import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Clock } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { analyticsAPI } from '../../../services/api';

const HorariosPico = ({ filtros }) => {
  const { darkMode } = useTheme();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    const params = {};
    if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros?.dataFim) params.dataFim = filtros.dataFim;
    if (filtros?.instanciaId) params.instanciaId = filtros.instanciaId;

    analyticsAPI.getHorariosPico(clienteId, params)
      .then(res => {
        // Preencher todas as 24 horas
        const map = {};
        (res.data || []).forEach(d => { map[d.hora] = d.total; });
        const full = Array.from({ length: 24 }, (_, i) => ({
          hora: i,
          label: `${String(i).padStart(2, '0')}h`,
          total: map[i] || 0,
        }));
        setDados(full);
      })
      .catch(() => setDados([]))
      .finally(() => setLoading(false));
  }, [clienteId, filtros?.dataInicio, filtros?.dataFim, filtros?.instanciaId]);

  if (loading) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl animate-pulse ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        <div className={`h-5 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded w-1/4 mb-4 sm:mb-6`}></div>
        <div className={`h-64 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded`}></div>
      </div>
    );
  }

  const totalMsgs = dados.reduce((s, d) => s + d.total, 0);
  if (totalMsgs === 0) return null;

  const max = Math.max(...dados.map(d => d.total));
  const picoHora = dados.find(d => d.total === max);

  const getBarColor = (total) => {
    const ratio = total / (max || 1);
    if (ratio > 0.75) return '#ef4444'; // vermelho = pico
    if (ratio > 0.4) return '#f59e0b';  // amarelo = moderado
    return '#3b82f6';                     // azul = tranquilo
  };

  return (
    <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
        <h3 className={`text-base sm:text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Clock className="w-4 h-4 sm:w-5 sm:h-5" /> Horários de Pico
        </h3>
        {picoHora && (
          <span className={`text-xs px-2 py-1 rounded-lg whitespace-nowrap ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
            🔥 Pico {picoHora.label} ({picoHora.total})
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={dados} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
          <XAxis dataKey="label" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }} interval={1} />
          <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: darkMode ? '#1f2937' : '#fff',
              border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: darkMode ? '#fff' : '#000',
            }}
            formatter={(value) => [`${value} mensagens`, 'Total']}
          />
          <Bar dataKey="total" radius={[4, 4, 0, 0]}>
            {dados.map((entry, i) => (
              <Cell key={i} fill={getBarColor(entry.total)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className={`flex items-center justify-center gap-6 mt-4 pt-4 border-t text-xs ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500"></span> Tranquilo</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-yellow-500"></span> Moderado</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500"></span> Pico</span>
      </div>
    </div>
  );
};

export default HorariosPico;
